import React from "react";
import { toast } from "react-toastify";
import {
  BsBellFill,
  BsChatDotsFill,
  BsCheckCircleFill,
  BsXCircleFill,
  BsFileEarmarkTextFill,
  BsCreditCardFill,
} from "react-icons/bs";

interface NotificationParams {
  data: any;
  userId: string | number;
  login_type: string;
  account_type: string;
  navigate: any;
}

const getNotificationMetadata = (type: string) => {
  switch (type) {
    case "document_validation_failed":
    case "proposal_rejected":
    case "subscription_expired":
      return {
        category: "Alert",
        variantClass: "fcm-toast-danger",
        Icon: BsXCircleFill,
      };
    case "document_validation_success":
    case "profile_approved":
    case "proposal_accepted":
    case "subscription_activated":
      return {
        category: "Success",
        variantClass: "fcm-toast-success",
        Icon: BsCheckCircleFill,
      };
    case "new_chat_message":
    case "mentor_chat_message":
    case "new_message":
      return {
        category: "Message",
        variantClass: "fcm-toast-chat",
        Icon: BsChatDotsFill,
      };
    case "proposal_received":
      return {
        category: "Proposal",
        variantClass: "fcm-toast-proposal",
        Icon: BsFileEarmarkTextFill,
      };
    case "subscription_expiry_reminder":
      return {
        category: "Subscription",
        variantClass: "fcm-toast-warning",
        Icon: BsCreditCardFill,
      };
    default:
      return {
        category: "Notification",
        variantClass: "fcm-toast-info",
        Icon: BsBellFill,
      };
  }
};

interface FCMNotificationCardProps {
  title?: string;
  body?: string;
  type?: string;
}

const FCMNotificationCard: React.FC<FCMNotificationCardProps> = ({
  title,
  body,
  type = "",
}) => {
  const { category, variantClass, Icon } = getNotificationMetadata(type);

  return (
    <div className={`fcm-notification-card ${variantClass}`}>
      <div className="fcm-notification-icon-wrapper">
        <Icon />
      </div>
      <div className="fcm-notification-content">
        <div className="fcm-notification-header">
          <span className="fcm-notification-badge">
            <span className="fcm-notification-pulse" />
            {category}
          </span>
          <span className="fcm-notification-time">Just now</span>
        </div>
        {title && <h4 className="fcm-notification-title">{title}</h4>}
        {body && <p className="fcm-notification-body">{body}</p>}
      </div>
    </div>
  );
};

export const handleFCMNotification = ({
  data,
  userId,
  login_type,
  account_type,
  navigate,
}: NotificationParams) => {
  console.log("FCM Notification Received:", data);

  if (!data || data?.user_id?.toString() !== userId?.toString()) return;

  const { title, body, type, chat_id } = data;

  const toastOptions = {
    className: "fcm-toast-container-custom",
    autoClose: 5000,
    closeOnClick: true,
    icon: false as const,
    onClick: () => {
      if (
        type === "new_message" ||
        type === "mentor_chat_message" ||
        type === "new_chat_message"
      ) {
        if (login_type === "investor") {
          navigate(`/investor/chats/${chat_id}`);
        } else {
          navigate(`/entrepreneur/${account_type}/chats/${chat_id}`);
        }
      } else if (
        type === "document_validation_failed" ||
        type === "document_verified"
      ) {
        if (login_type === "investor") {
          navigate(`/investor/my-profile`);
        } else {
          navigate(`/entrepreneur/${account_type}/my-profile`);
        }
      } else if (type === "proposal_received") {
        navigate(`/investor/my-proposals`);
      } else if (type === "proposal_accepted" || type === "proposal_rejected") {
        navigate(`/entrepreneur/${account_type}/sent-proposals`);
      } else if (
        type === "subscription_expired" ||
        type === "subscription_activated" ||
        type === "subscription_expiry_reminder"
      ) {
        navigate(`/entrepreneur/${account_type}/my-subscription`);
      }
    },
  };

  toast(
    <FCMNotificationCard title={title} body={body} type={type} />,
    toastOptions
  );

  // 🔔 Dispatch event only if chat type
  if (
    type === "new_message" ||
    type === "mentor_chat_message" ||
    type === "new_chat_message"
  ) {
    const customEvent = new CustomEvent("newChatMessage", {
      detail: {
        message: body,
        chat_uuid: chat_id,
        user_id: data?.user_id,
      },
    });

    window.dispatchEvent(customEvent);
  }
};
